import {
  useCallback, useEffect, useMemo, useRef, useState,
  type ChangeEvent, type UIEvent,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { insertNoteLine, type NoteSelection } from '@brunel/shared';
import { EMPTY_NOTE_CALCULATOR, NoteCalculator, type NoteCalculatorState } from './NoteCalculator';
import { stageSelectedNoteHandoff, type NoteHandoffStage } from '../lib/note-handoff';
import {
  NOTE_AUTOSAVE_MS,
  archiveOwnerNotePage,
  createOwnerNotePage,
  loadOwnerNotePages,
  noteCounts,
  noteLineNumbers,
  normalizedNoteTitle,
  scheduleOwnerNoteAutosave,
  sortOwnerNotePages,
  updateOwnerNotePage,
  type OwnerNotePage,
} from '../lib/owner-notes';
import { useReloadCheck } from '../lib/safe-reload';

function dateTime(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? 'Unknown'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

function pageDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? 'Unknown'
    : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(parsed);
}

export function NotePageEditor({
  page, saving, onTitle, onBody, onCommit, onDiscuss, onSelection,
}: {
  readonly page: OwnerNotePage;
  readonly saving: boolean;
  readonly onTitle: (title: string) => void;
  readonly onBody: (body: string) => void;
  readonly onCommit: () => void;
  /** Stages this page only. The editor never sees the other notes. */
  readonly onDiscuss?: (staged: NoteHandoffStage) => void;
  /** Caret/selection in the body, so a calculation can be added where the owner was writing. */
  readonly onSelection?: (selection: NoteSelection) => void;
}) {
  const gutter = useRef<HTMLPreElement | null>(null);
  const counts = noteCounts(page.body);
  const syncGutter = (event: UIEvent<HTMLTextAreaElement>) => {
    if (gutter.current) gutter.current.scrollTop = event.currentTarget.scrollTop;
  };

  return (
    <div className="notepad-editor stack" data-testid="notepad-editor" data-reload-preserved="notepad">
      <input
        className="notepad-title"
        aria-label="Page title"
        value={page.title}
        maxLength={120}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onTitle(event.target.value)}
        onBlur={onCommit}
      />
      <div className="notepad-page-meta">
        <span>Created {dateTime(page.created_at)}</span>
        <span>Updated {dateTime(page.updated_at)}</span>
        <span className="notepad-save-state">{saving ? 'Saving…' : 'Autosaved'}</span>
      </div>
      <div className="notepad-text-shell">
        <pre ref={gutter} className="notepad-line-numbers" aria-hidden="true">
          {noteLineNumbers(page.body)}
        </pre>
        <textarea
          className="notepad-text"
          aria-label="Note body"
          value={page.body}
          wrap="soft"
          spellCheck
          onChange={(event) => onBody(event.target.value)}
          onScroll={syncGutter}
          onSelect={(event) => onSelection?.({
            start: event.currentTarget.selectionStart,
            end: event.currentTarget.selectionEnd,
          })}
          onBlur={onCommit}
          placeholder="Write a developer note…"
        />
      </div>
      <div className="notepad-counts" aria-label="Note counts">
        <span>Lines <strong>{counts.lines}</strong></span>
        <span>Words <strong>{counts.words}</strong></span>
        <span>Letters <strong>{counts.letters}</strong></span>
      </div>
      <button
        type="button"
        className="primary notepad-discuss"
        aria-label="Discuss with AI"
        title="Send to Main Command"
        onClick={() => onDiscuss?.(stageSelectedNoteHandoff(page))}
      >
        Discuss with AI
      </button>
    </div>
  );
}

export function DeveloperNotepad({
  client, orgId, onDiscuss,
}: {
  readonly client: SupabaseClient;
  readonly orgId: string;
  /** Explicit handoff of the selected note. Does not send and does not load other notes. */
  readonly onDiscuss?: (staged: NoteHandoffStage) => void;
}) {
  const [pages, setPages] = useState<OwnerNotePage[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [calculator, setCalculator] = useState<NoteCalculatorState>(EMPTY_NOTE_CALCULATOR);
  const [calculatorStatus, setCalculatorStatus] = useState<string | null>(null);
  /** Last caret in the editor, tied to the page it belongs to. */
  const noteSelection = useRef<(NoteSelection & { readonly pageId: string }) | null>(null);
  const dirty = useRef(new Set<string>());
  const latestPages = useRef<OwnerNotePage[]>([]);
  const activeIdRef = useRef<string | null>(null);

  // #856: the page text is kept on the server; only a change not yet saved there holds an update.
  useReloadCheck(() => dirty.current.size > 0 || savingId !== null
    ? { reason: 'Notepad changes are still saving', preserved: false }
    : null);

  useEffect(() => { latestPages.current = pages; }, [pages]);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const loaded = await loadOwnerNotePages(client, orgId);
      setPages(loaded);
      setActiveId((current) => loaded.some((page) => page.id === current)
        ? current
        : loaded[0]?.id ?? null);
      dirty.current.clear();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load notes.');
    } finally {
      setLoading(false);
    }
  }, [client, orgId]);

  useEffect(() => {
    setPages([]);
    setActiveId(null);
    dirty.current.clear();
    void refresh();
  }, [refresh]);

  const active = useMemo(
    () => pages.find((page) => page.id === activeId) ?? null,
    [pages, activeId],
  );

  const persist = useCallback(async (snapshot: OwnerNotePage): Promise<boolean> => {
    if (!dirty.current.has(snapshot.id)) return true;
    setSavingId(snapshot.id);
    setError(null);
    try {
      const saved = await updateOwnerNotePage(client, orgId, snapshot);
      setPages((current) => sortOwnerNotePages(current.map((page) => {
        if (page.id !== snapshot.id) return page;
        const unchanged = page.title === snapshot.title && page.body === snapshot.body;
        if (!unchanged) return page;
        dirty.current.delete(page.id);
        return { ...page, title: saved.title, updated_at: saved.updated_at };
      })));
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not autosave this page.');
      return false;
    } finally {
      setSavingId((current) => current === snapshot.id ? null : current);
    }
  }, [client, orgId]);

  useEffect(() => {
    if (active === null || !dirty.current.has(active.id)) return;
    return scheduleOwnerNoteAutosave(() => { void persist(active); }, NOTE_AUTOSAVE_MS);
  }, [active?.id, active?.title, active?.body, persist]);

  useEffect(() => () => {
    const page = latestPages.current.find((row) => row.id === activeIdRef.current);
    if (page && dirty.current.has(page.id)) void updateOwnerNotePage(client, orgId, page);
  }, [client, orgId]);

  const updateActive = (patch: Partial<Pick<OwnerNotePage, 'title' | 'body'>>) => {
    if (activeId === null) return;
    dirty.current.add(activeId);
    setPages((current) => current.map((page) =>
      page.id === activeId ? { ...page, ...patch } : page));
  };

  // Goes through the same dirty/autosave path as typing. The selection is used
  // only when it belongs to the page being written, so a switch between pages
  // can never place a calculation in the wrong note.
  const addCalculation = (line: string) => {
    const page = latestPages.current.find((row) => row.id === activeIdRef.current);
    if (!page) return;
    const selection = noteSelection.current?.pageId === page.id ? noteSelection.current : null;
    const inserted = insertNoteLine(page.body, line, selection);
    noteSelection.current = { pageId: page.id, start: inserted.cursor, end: inserted.cursor };
    dirty.current.add(page.id);
    setPages((current) => current.map((row) => row.id === page.id ? { ...row, body: inserted.body } : row));
    setCalculator((current) => ({ ...current, label: '' }));
    setCalculatorStatus(`Added to “${normalizedNoteTitle(page.title)}”.`);
  };

  const changeCalculator = (next: NoteCalculatorState) => {
    setCalculator(next);
    setCalculatorStatus(null);
  };

  const selectPage = async (pageId: string) => {
    if (pageId === activeId) return;
    if (active && dirty.current.has(active.id) && !(await persist(active))) return;
    setArchiveConfirmId(null);
    setCalculatorStatus(null);
    setActiveId(pageId);
  };

  const createPage = async () => {
    if (creating) return;
    if (active && dirty.current.has(active.id) && !(await persist(active))) return;
    setCreating(true);
    setError(null);
    try {
      const page = await createOwnerNotePage(client, orgId);
      setPages((current) => sortOwnerNotePages([page, ...current]));
      setActiveId(page.id);
      setArchiveConfirmId(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create a page.');
    } finally {
      setCreating(false);
    }
  };

  const archivePage = async (page: OwnerNotePage) => {
    setError(null);
    if (dirty.current.has(page.id) && !(await persist(page))) return;
    try {
      await archiveOwnerNotePage(client, orgId, page.id);
      dirty.current.delete(page.id);
      setPages((current) => {
        const next = current.filter((row) => row.id !== page.id);
        if (activeId === page.id) setActiveId(next[0]?.id ?? null);
        return next;
      });
      setArchiveConfirmId(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not archive this page.');
    }
  };

  const calculatorPanel = (
    <NoteCalculator
      state={calculator}
      onChange={changeCalculator}
      onAdd={addCalculation}
      targetTitle={active ? normalizedNoteTitle(active.title) : null}
      onCreatePage={() => { void createPage(); }}
      creating={creating}
      status={calculatorStatus}
    />
  );

  if (loading) return <div className="notepad-empty" role="status">Loading notes…</div>;

  return (
    <section className="developer-notepad" aria-label="Developer notes" data-testid="developer-notepad">
      <div className="notepad-toolbar">
        <select
          className="notepad-page-select"
          aria-label="Note page"
          value={activeId ?? ''}
          onChange={(event) => { void selectPage(event.target.value); }}
        >
          {pages.length === 0 && <option value="">No pages</option>}
          {pages.map((page) => <option key={page.id} value={page.id}>{page.title}</option>)}
        </select>
        <button type="button" className="primary notepad-new-page" disabled={creating} onClick={() => { void createPage(); }}>
          {creating ? 'Creating…' : '+ New page'}
        </button>
      </div>

      {pages.length === 0 ? (
        <div className="notepad-empty stack" role="status">
          <p>No note pages yet.</p>
          <p className="muted">Create a plain-text scratch page. Notes stay private to your user in this organization.</p>
          {calculatorPanel}
        </div>
      ) : (
        <div className="notepad-layout">
          <nav className="notepad-page-list" aria-label="Note pages">
            {pages.map((page) => (
              <button
                type="button"
                key={page.id}
                className={page.id === activeId ? 'notepad-page on' : 'notepad-page'}
                aria-current={page.id === activeId ? 'page' : undefined}
                onClick={() => { void selectPage(page.id); }}
                title={`Created ${dateTime(page.created_at)} · Updated ${dateTime(page.updated_at)}`}
              >
                <strong>{page.title || 'Untitled page'}</strong>
                <small>{pageDate(page.updated_at)}</small>
              </button>
            ))}
          </nav>

          <div className="notepad-main">
            {active && (
              <>
                <NotePageEditor
                  page={active}
                  saving={savingId === active.id || dirty.current.has(active.id)}
                  onTitle={(title) => updateActive({ title })}
                  onBody={(body) => updateActive({ body })}
                  onCommit={() => { void persist(active); }}
                  onDiscuss={onDiscuss}
                  onSelection={(selection) => { noteSelection.current = { ...selection, pageId: active.id }; }}
                />
                {calculatorPanel}
                {archiveConfirmId === active.id ? (
                  <div className="notepad-archive-confirm" role="alert">
                    <p>Archive “{normalizedNoteTitle(active.title)}”?</p>
                    <small className="muted">It leaves the active notebook but remains a server record.</small>
                    <div className="row wrap">
                      <button type="button" className="danger" onClick={() => { void archivePage(active); }}>
                        Archive page
                      </button>
                      <button type="button" onClick={() => setArchiveConfirmId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="ghost notepad-archive"
                    onClick={() => setArchiveConfirmId(active.id)}>Archive page…</button>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {error !== null && <p className="notepad-error" role="alert">{error}</p>}
    </section>
  );
}
